import type { ExperienciaProduto, RecursoProduto, RecursosProduto } from "@/lib/experiencia-produto";
import type { ModuloFollowProUsuario } from "@/lib/followpro-acesso-valores";

type Papel = "PLATFORM_ADMIN" | "GESTOR" | "USUARIO";
type SuperficieNavegacao = "desktop" | "mobile";
type RegraAbertura =
  | { tipo: "prefixo"; valor: string; incluirRaiz: boolean }
  | { tipo: "rotas"; valores: readonly string[] };

export type FollowZapModuloId =
  | "inicio"
  | "fluxos"
  | "driver"
  | "follow-up"
  | "etiquetas"
  | "crm"
  | "carteira"
  | "produtos"
  | "captacao"
  | "integracoes"
  | "leads"
  | "equipe"
  | "analise"
  | "meta-ads"
  | "configuracoes";

export type FollowZapSubmenuId = "fluxos" | "driver" | "captacao" | "captacao-followpro" | "leads" | "leads-followpro" | "leads-followpro-usuario" | "equipe" | "analise" | "analise-equipe" | "meta-ads";

export type ItemSubmenuFollowZap = {
  id: string;
  href: string;
  label: string;
  aliases?: readonly string[];
};

export type ModuloFollowZap = {
  id: FollowZapModuloId;
  ordem: number;
  href: string;
  label: string;
  mobileLabel: string;
  recurso?: RecursoProduto;
  submenu?: FollowZapSubmenuId;
  requerModoAvancado?: boolean;
  requerFollowUpVisivel?: boolean;
  desktopOnly?: boolean;
  shellExperimental: "principal" | "mais" | "direita";
};

export const SUBMENUS_FOLLOWZAP = {
  fluxos: {
    abertura: {
      desktop: { tipo: "prefixo", valor: "/profissional/", incluirRaiz: false },
      mobile: { tipo: "prefixo", valor: "/profissional/", incluirRaiz: false },
    },
    itens: [
      { id: "mensagens", href: "/profissional/mensagens", label: "Mensagens" },
      { id: "audios", href: "/profissional/audios", label: "Áudios" },
      { id: "midias", href: "/profissional/midias", label: "Mídias" },
      { id: "funis", href: "/profissional/funis", label: "Funis" },
      { id: "favoritos", href: "/profissional/favoritos", label: "Favoritos" },
      { id: "etiquetas", href: "/profissional/etiquetas", label: "Etiquetas" },
    ],
  },
  driver: {
    abertura: {
      desktop: { tipo: "rotas", valores: ["/drive", "/drive/pessoal", "/drive/meu-driver", "/drive/lixeira"] },
      mobile: { tipo: "rotas", valores: ["/drive", "/drive/pessoal", "/drive/meu-driver", "/drive/lixeira"] },
    },
    itens: [
      { id: "pessoal", href: "/drive/pessoal", label: "Pessoal", aliases: ["/drive"] },
      { id: "meu-driver", href: "/drive/meu-driver", label: "Meu Driver" },
      { id: "lixeira", href: "/drive/lixeira", label: "Lixeira" },
    ],
  },
  captacao: {
    abertura: {
      desktop: { tipo: "prefixo", valor: "/captacao/", incluirRaiz: true },
      mobile: { tipo: "prefixo", valor: "/captacao/", incluirRaiz: false },
    },
    itens: [
      { id: "formulario-meta", href: "/captacao/formulario-meta", label: "Formulário Meta" },
      { id: "followzap", href: "/captacao/followzap", label: "FollowZap" },
    ],
  },
  "captacao-followpro": {
    abertura: {
      desktop: { tipo: "rotas", valores: ["/captacao/formulario-meta", "/captacao/followzap"] },
      mobile: { tipo: "rotas", valores: ["/captacao/formulario-meta", "/captacao/followzap"] },
    },
    itens: [
      { id: "formulario-meta", href: "/captacao/formulario-meta", label: "Formulário Meta" },
      { id: "followzap", href: "/captacao/followzap", label: "FollowZap" },
    ],
  },
  leads: {
    abertura: {
      desktop: { tipo: "prefixo", valor: "/leads/", incluirRaiz: true },
      mobile: { tipo: "prefixo", valor: "/leads/", incluirRaiz: false },
    },
    itens: [
      { id: "lista", href: "/leads/lista", label: "Lista" },
      { id: "historico", href: "/leads/historico", label: "Histórico" },
    ],
  },
  "leads-followpro": {
    abertura: {
      desktop: { tipo: "prefixo", valor: "/leads/", incluirRaiz: false },
      mobile: { tipo: "prefixo", valor: "/leads/", incluirRaiz: false },
    },
    itens: [
      { id: "lista", href: "/leads/lista", label: "Lista", aliases: ["/leads/arquivadas", "/leads/lixeira"] },
      { id: "historico", href: "/leads/historico", label: "Histórico" },
    ],
  },
  "leads-followpro-usuario": {
    abertura: {
      desktop: { tipo: "rotas", valores: ["/leads/historico"] },
      mobile: { tipo: "rotas", valores: ["/leads/historico"] },
    },
    itens: [{ id: "historico", href: "/leads/historico", label: "Histórico" }],
  },
  equipe: {
    abertura: {
      desktop: { tipo: "prefixo", valor: "/followpro/equipe/", incluirRaiz: false },
      mobile: { tipo: "prefixo", valor: "/followpro/equipe/", incluirRaiz: false },
    },
    itens: [
      { id: "integrantes", href: "/followpro/equipe/integrantes", label: "Integrantes" },
    ],
  },
  analise: {
    abertura: {
      desktop: { tipo: "prefixo", valor: "/analise/", incluirRaiz: true },
      mobile: { tipo: "prefixo", valor: "/analise/", incluirRaiz: true },
    },
    itens: [
      { id: "vendas", href: "/analise/rendimento", label: "Vendas" },
      { id: "leads", href: "/analise/leads", label: "Leads" },
      { id: "distribuicao", href: "/analise/entrada-distribuicao", label: "Distribuição" },
      { id: "atendimento", href: "/analise/atendimento", label: "Atendimento" },
    ],
  },
  // Gestor de equipe: a Análise sem a aba Leads de captação (Meta Lead Ads e FollowZap).
  "analise-equipe": {
    abertura: {
      desktop: { tipo: "prefixo", valor: "/analise/", incluirRaiz: true },
      mobile: { tipo: "prefixo", valor: "/analise/", incluirRaiz: true },
    },
    itens: [
      { id: "vendas", href: "/analise/rendimento", label: "Vendas" },
      { id: "distribuicao", href: "/analise/entrada-distribuicao", label: "Distribuição" },
      { id: "atendimento", href: "/analise/atendimento", label: "Atendimento" },
    ],
  },
  "meta-ads": {
    abertura: {
      desktop: { tipo: "prefixo", valor: "/meta-ads/", incluirRaiz: true },
      mobile: { tipo: "prefixo", valor: "/meta-ads/", incluirRaiz: false },
    },
    itens: [
      { id: "campanhas", href: "/meta-ads/campanhas", label: "Campanhas" },
      { id: "objetivos", href: "/meta-ads/objetivos", label: "Objetivos" },
      { id: "contas-de-anuncio", href: "/meta-ads/contas-de-anuncio", label: "Contas de anúncio" },
    ],
  },
} as const satisfies Record<FollowZapSubmenuId, {
  abertura: Record<SuperficieNavegacao, RegraAbertura>;
  itens: readonly ItemSubmenuFollowZap[];
}>;

export const NAVEGACAO_FOLLOWZAP = {
  produtos: ["FOLLOW_ZAP"],
  papeis: ["USUARIO"],
  modulos: [
    { id: "inicio", ordem: 1, href: "/", label: "Início", mobileLabel: "Início", recurso: "inicio", shellExperimental: "principal" },
    { id: "fluxos", ordem: 2, href: "/profissional/mensagens", label: "Fluxos", mobileLabel: "Fluxos", recurso: "biblioteca", submenu: "fluxos", shellExperimental: "principal" },
    { id: "driver", ordem: 3, href: "/drive", label: "Driver", mobileLabel: "Driver", submenu: "driver", shellExperimental: "principal" },
    { id: "follow-up", ordem: 4, href: "/follow-up", label: "Follow-up", mobileLabel: "Follow-up", recurso: "followUp", requerFollowUpVisivel: true, shellExperimental: "principal" },
    { id: "etiquetas", ordem: 5, href: "/etiquetas", label: "Etiquetas", mobileLabel: "Etiquetas", recurso: "etiquetas", shellExperimental: "mais" },
    { id: "crm", ordem: 6, href: "/crm", label: "CRM", mobileLabel: "CRM", recurso: "crm", shellExperimental: "principal" },
    { id: "carteira", ordem: 7, href: "/carteira", label: "Carteira", mobileLabel: "Carteira", recurso: "carteira", shellExperimental: "mais" },
    { id: "produtos", ordem: 8, href: "/produtos", label: "Produtos", mobileLabel: "Produtos", recurso: "vendas", shellExperimental: "mais" },
    { id: "captacao", ordem: 9, href: "/captacao", label: "Captação", mobileLabel: "Captação", recurso: "integracoes", submenu: "captacao", shellExperimental: "mais" },
    { id: "integracoes", ordem: 10, href: "/integracoes", label: "Integrações", mobileLabel: "Integrações", recurso: "integracoes", shellExperimental: "mais" },
    { id: "leads", ordem: 11, href: "/leads", label: "Leads", mobileLabel: "Leads", recurso: "leads", submenu: "leads", shellExperimental: "principal" },
    { id: "analise", ordem: 12, href: "/analise", label: "Análise", mobileLabel: "Análise", recurso: "analise", submenu: "analise", shellExperimental: "principal" },
    { id: "meta-ads", ordem: 13, href: "/meta-ads", label: "Meta Ads", mobileLabel: "Meta Ads", recurso: "anuncios", submenu: "meta-ads", shellExperimental: "mais" },
    { id: "configuracoes", ordem: 14, href: "/configuracoes", label: "Configurações", mobileLabel: "Config.", recurso: "configuracoes", desktopOnly: true, shellExperimental: "direita" },
  ],
} as const satisfies {
  produtos: readonly ExperienciaProduto["produto"][];
  papeis: readonly Papel[];
  modulos: readonly ModuloFollowZap[];
};

const MODULO_EQUIPE_FOLLOWPRO: ModuloFollowZap = {
  id: "equipe", ordem: 11, href: "/followpro/equipe/integrantes", label: "Equipe",
  mobileLabel: "Equipe", submenu: "equipe", shellExperimental: "principal",
};

const MODULO_EQUIPES_FOLLOWPRO: ModuloFollowZap = {
  id: "equipe", ordem: 11, href: "/followpro/equipes", label: "Equipes",
  mobileLabel: "Equipes", shellExperimental: "principal",
};

const MODULO_INICIO_FOLLOWPRO: ModuloFollowZap = {
  id: "inicio", ordem: 1, href: "/", label: "Início",
  mobileLabel: "Início", shellExperimental: "principal",
};

const MODULO_CAPTACAO_FOLLOWPRO: ModuloFollowZap = {
  id: "captacao", ordem: 9, href: "/captacao/formulario-meta", label: "Captação",
  mobileLabel: "Captação", submenu: "captacao-followpro", shellExperimental: "mais",
};

const MODULO_LEADS_FOLLOWPRO: ModuloFollowZap = {
  id: "leads", ordem: 11, href: "/leads/lista", label: "Leads",
  mobileLabel: "Leads", submenu: "leads-followpro", shellExperimental: "principal",
};

const MODULO_LEADS_FOLLOWPRO_USUARIO: ModuloFollowZap = {
  id: "leads", ordem: 11, href: "/leads/historico", label: "Leads",
  mobileLabel: "Leads", submenu: "leads-followpro-usuario", shellExperimental: "principal",
};

function ehRotaAnaliseOuConfiguracoes(pathname: string) {
  return pathname === "/analise" || pathname.startsWith("/analise/") || pathname === "/configuracoes";
}

export function ehAreaAnaliseGestorFollowZap(pathname: string, produto?: string | null, papel?: string | null) {
  return produto === "FOLLOW_ZAP" && papel === "GESTOR" && ehRotaAnaliseOuConfiguracoes(pathname);
}

export function ehAreaGerencialFollowPro(pathname: string, produto?: string | null, papel?: string | null) {
  return produto === "FOLLOW_PRO" && papel === "GESTOR" &&
    (pathname === "/" || ehRotaAnaliseOuConfiguracoes(pathname) || pathname === "/drive" || pathname.startsWith("/drive/") || pathname === "/follow-up" || pathname === "/followpro/equipe/integrantes" || pathname === "/followpro/equipes" || pathname === "/leads/lista" || pathname === "/leads/historico" || pathname === "/leads/arquivadas" || pathname === "/leads/lixeira" || pathname === "/produtos" || pathname === "/etiquetas" || pathname === "/captacao/formulario-meta" || pathname === "/captacao/followzap" || pathname === "/meta-ads" || pathname.startsWith("/meta-ads/"));
}

export function destinoEntradaGestor(produto: string) {
  return produto === "FOLLOW_ZAP" ? "/analise" : produto === "FOLLOW_PRO" ? "/" : "/gestor/inicio";
}

export function menuOcultaConfiguracoes(produto?: string | null, papel?: string | null) {
  return produto === "FOLLOW_ZAP" || (produto === "FOLLOW_PRO" && papel === "GESTOR");
}

export function podeMostrarConfiguracoesMenuPerfil(produto?: string | null, papel?: string | null, recursoConfiguracoes = false) {
  return recursoConfiguracoes && (
    (produto === "FOLLOW_ZAP" && (papel === "USUARIO" || papel === "GESTOR")) ||
    (produto === "FOLLOW_PRO" && papel === "GESTOR")
  );
}

export function podeMostrarNumerosMenuPerfil(produto?: string | null, papel?: string | null, recursoNumeros = false) {
  return papel === "USUARIO" && (produto === "FOLLOW_PRO" || (produto === "FOLLOW_ZAP" && recursoNumeros));
}

export type ContextoMeta = "individual" | "gestao";

const META_INDIVIDUAL = ["individual"] as const;
const METAS_GESTOR = ["individual", "gestao"] as const;
const SEM_META: readonly ContextoMeta[] = [];

export function contextosMetaMenuPerfil(produto?: string | null, papel?: string | null, recursoConfiguracoes = false): readonly ContextoMeta[] {
  if (produto === "FOLLOW_PRO" && papel === "USUARIO" && recursoConfiguracoes) return META_INDIVIDUAL;
  if (!podeMostrarConfiguracoesMenuPerfil(produto, papel, recursoConfiguracoes)) return SEM_META;
  return papel === "GESTOR" ? METAS_GESTOR : META_INDIVIDUAL;
}

export function modulosFollowZapParaContexto(contexto: {
  produto: ExperienciaProduto["produto"];
  papel: Papel | null;
  recursos: Readonly<RecursosProduto>;
  modoAvancado: boolean;
  mostrarFollowUp: boolean;
  areaAnaliseGestorFollowZap?: boolean;
  areaGerencialAnalise?: boolean;
  modulosFollowProUsuario?: readonly ModuloFollowProUsuario[];
  escopoGestor?: "DONO" | "GESTOR_EQUIPE" | "NENHUM" | null;
  ehDono?: boolean;
  plano?: number;
}) {
  if (contexto.produto === "FOLLOW_ZAP" && contexto.papel === "GESTOR" && contexto.areaAnaliseGestorFollowZap) {
    if (!contexto.recursos.analise) return [];
    return NAVEGACAO_FOLLOWZAP.modulos.filter((modulo) => modulo.id === "analise");
  }
  if (contexto.produto === "FOLLOW_PRO" && contexto.papel === "GESTOR" && contexto.areaGerencialAnalise) {
    // NENHUM restrito (gestor FOLLOW_PRO sem equipe ativa) não vê Análise, Meta Ads, Captação nem Leads.
    // GESTOR_EQUIPE vê Análise (sem a aba Leads de captação), mas não vê Meta Ads, Captação nem Leads. DONO mantém o menu completo.
    const gestorSemEquipe = contexto.escopoGestor === "NENHUM";
    const gestorEquipe = contexto.escopoGestor === "GESTOR_EQUIPE";
    const gestorRestrito = gestorSemEquipe || gestorEquipe;
    const modulos = NAVEGACAO_FOLLOWZAP.modulos.filter((modulo) =>
      (modulo.id === "analise" && contexto.recursos.analise && !gestorSemEquipe) ||
      modulo.id === "driver" ||
      modulo.id === "follow-up" ||
      (modulo.id === "etiquetas" && contexto.recursos.etiquetas) ||
      (modulo.id === "produtos" && contexto.recursos.vendas) ||
      (modulo.id === "meta-ads" && !gestorRestrito))
      .map((modulo) => modulo.id === "analise" && gestorEquipe ? { ...modulo, submenu: "analise-equipe" as const } : modulo);
    // Ponto de inserção da entrada Equipes (DONO com plano > 1): logo após MODULO_EQUIPE_FOLLOWPRO.
    const equipesDono = contexto.ehDono === true && (contexto.plano ?? 0) > 1;
    return [MODULO_INICIO_FOLLOWPRO, ...modulos.filter((modulo) => modulo.id === "driver"), MODULO_EQUIPE_FOLLOWPRO, ...(equipesDono ? [MODULO_EQUIPES_FOLLOWPRO] : []), ...modulos.filter((modulo) => modulo.id !== "driver"), ...(gestorRestrito ? [] : [MODULO_CAPTACAO_FOLLOWPRO, MODULO_LEADS_FOLLOWPRO])];
  }
  if (contexto.produto === "FOLLOW_PRO" && contexto.papel === "USUARIO") {
    return [
      MODULO_INICIO_FOLLOWPRO,
      ...(contexto.modulosFollowProUsuario?.includes("fluxos")
        ? NAVEGACAO_FOLLOWZAP.modulos.filter((modulo) => modulo.id === "fluxos") : []),
      ...(contexto.modulosFollowProUsuario?.includes("driver")
        ? NAVEGACAO_FOLLOWZAP.modulos.filter((modulo) => modulo.id === "driver") : []),
      ...(contexto.modulosFollowProUsuario?.includes("follow-up")
        ? NAVEGACAO_FOLLOWZAP.modulos.filter((modulo) => modulo.id === "follow-up") : []),
      ...(contexto.modulosFollowProUsuario?.includes("historico") ? [MODULO_LEADS_FOLLOWPRO_USUARIO] : []),
      ...(contexto.modulosFollowProUsuario?.includes("carteira")
        ? NAVEGACAO_FOLLOWZAP.modulos.filter((modulo) => modulo.id === "carteira") : []),
      ...(contexto.modulosFollowProUsuario?.includes("etiquetas") && contexto.recursos.etiquetas
        ? NAVEGACAO_FOLLOWZAP.modulos.filter((modulo) => modulo.id === "etiquetas") : []),
      ...(contexto.modulosFollowProUsuario?.includes("produtos") && contexto.recursos.vendas
        ? NAVEGACAO_FOLLOWZAP.modulos.filter((modulo) => modulo.id === "produtos") : []),
    ];
  }
  if (contexto.produto !== "FOLLOW_ZAP" || contexto.papel !== "USUARIO") return null;

  return NAVEGACAO_FOLLOWZAP.modulos.filter((modulo) =>
    (!("recurso" in modulo) || contexto.recursos[modulo.recurso]) &&
    (!("requerFollowUpVisivel" in modulo) || contexto.mostrarFollowUp) &&
    !(modulo.id === "configuracoes" && menuOcultaConfiguracoes(contexto.produto, contexto.papel)));
}

export function hrefModuloFollowZapAtivo(pathname: string, modulos: readonly Pick<ModuloFollowZap, "href">[]) {
  return modulos
    .filter(({ href }) => pathname === href || (href !== "/" && pathname.startsWith(href + "/")))
    .sort((a, b) => b.href.length - a.href.length)[0]?.href;
}

function correspondeARegra(pathname: string, regra: RegraAbertura) {
  if (regra.tipo === "rotas") return regra.valores.includes(pathname);
  const raiz = regra.valor.endsWith("/") ? regra.valor.slice(0, -1) : regra.valor;
  return pathname.startsWith(regra.valor) || (regra.incluirRaiz && pathname === raiz);
}

export function submenuFollowZapDoPathname(
  pathname: string,
  superficie: SuperficieNavegacao,
  modulos: readonly Pick<ModuloFollowZap, "id" | "submenu">[],
) {
  for (const modulo of modulos) {
    if (!modulo.submenu) continue;
    const submenu = SUBMENUS_FOLLOWZAP[modulo.submenu];
    if (correspondeARegra(pathname, submenu.abertura[superficie])) return { moduloId: modulo.id, ...submenu };
  }
  return null;
}

export function moduloFollowZapAtivo(pathname: string, modulos: readonly ModuloFollowZap[]) {
  const submenu = submenuFollowZapDoPathname(pathname, "desktop", modulos);
  if (submenu) return modulos.find(({ id }) => id === submenu.moduloId);
  const href = hrefModuloFollowZapAtivo(pathname, modulos);
  return modulos.find((modulo) => modulo.href === href);
}

export function itemSubmenuFollowZapAtivo(pathname: string, item: Pick<ItemSubmenuFollowZap, "href" | "aliases">) {
  return pathname === item.href || item.aliases?.includes(pathname) === true;
}

export const ITENS_DRIVER_FOLLOWZAP = SUBMENUS_FOLLOWZAP.driver.itens;
